import {
  FetchAgencyContextType,
  AgencyNode,
  IAgencyGridSelectProps,
  IAgencyGridSelectPropsContextType,
} from 'modals/ShiftModal/components/AgencyGridSelect/AgencyGridSelect.types';
import { Context, createContext, FC, useContext, useMemo } from 'react';
import { AgencyCommunity, Community } from 'types/graphql';

export const AgencyGridSelectPropsContext = createContext<IAgencyGridSelectPropsContextType>(
  {} as IAgencyGridSelectPropsContextType,
);

export const AgencyGridSelectPropsProvider: FC<IAgencyGridSelectProps> = ({
  onChangeForm,
  fetchAgenciesContext,
  errorMessage,
  type,
  selected,
  name,
  positionId,
  children,
}) => {
  const { data, loadMore } = useContext<FetchAgencyContextType>(
    fetchAgenciesContext as unknown as Context<FetchAgencyContextType>,
  );

  const agencyList = useMemo(() => {
    if (data?.combinedNodes) {
      return data.combinedNodes as AgencyNode[];
    }

    const marketplaceNodes = data?.fetchMarketplaceCommunities?.nodes || [];
    const agencyNodes = data?.fetchAgencyCommunities?.nodes || [];

    return [
      ...marketplaceNodes.map((n: Community) => ({ ...n, sourceType: 'marketplace' })),
      ...agencyNodes.map((n: AgencyCommunity) => ({ ...n, sourceType: 'agency' })),
    ] as AgencyNode[];
  }, [data]);

  const contextValue = useMemo(
    () => ({
      hasNextPage:
        !!data?.fetchMarketplaceCommunities?.pageInfo?.hasNextPage ||
        !!data?.fetchAgencyCommunities?.pageInfo?.hasNextPage,
      agencyList: agencyList as AgencyNode[],
      onChangeForm,
      loadMore,
      errorMessage,
      type,
      selected,
      name,
      positionId,
    }),
    [
      agencyList,
      data?.fetchAgencyCommunities?.pageInfo?.hasNextPage,
      data?.fetchMarketplaceCommunities?.pageInfo?.hasNextPage,
      errorMessage,
      loadMore,
      name,
      onChangeForm,
      positionId,
      selected,
      type,
    ],
  );

  return (
    <AgencyGridSelectPropsContext.Provider value={contextValue}>
      {children}
    </AgencyGridSelectPropsContext.Provider>
  );
};

export const useAgencyGridSelectPropsContext = (): IAgencyGridSelectPropsContextType =>
  useContext(AgencyGridSelectPropsContext);
